'use strict';

/**
 * Pict-Provider-ShapesAPI
 * -----------------------
 * Browser-side client for the bundled shape libraries (Phosphor, Tabler, Brands). The server holds the
 * ~10k icons and serves each one recolored, so this provider is just transport: it searches the index
 * and builds the per-shape image URL. Moodboard-StickerSource turns the results into gallery items.
 *
 *   search(query, collection, limit) -> Promise<[ { Collection, Name, Title } ]>
 *   shapeURL(collection, name, colors) -> the GET URL for the recolored SVG (a card's <img> src)
 *   collectionList() / collection(key) -> static attribution (label, license, link) for the gallery
 */

const libPictProvider = require('pict').ServiceProviderBase;
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'ShapesAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/'
};

// Display labels + attribution, mirrored from the server's ShapeLibrary so the gallery can show a
// license line and a link without a round trip. Keys match the server collection keys.
const _COLLECTIONS =
{
	phosphor: { Label: 'Phosphor', License: 'MIT',     Link: 'https://phosphoricons.com' },
	tabler:   { Label: 'Tabler',   License: 'MIT',     Link: 'https://tabler.io/icons' },
	simple:   { Label: 'Brands',   License: 'CC0 1.0', Link: 'https://simpleicons.org' }
};

class PictProviderShapesAPI extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);
		this.routePrefix = this.options.RoutePrefix;
	}

	_url(pPath)
	{
		let tmpPrefix = this.routePrefix;
		if (tmpPrefix.charAt(tmpPrefix.length - 1) !== '/') { tmpPrefix += '/'; }
		return tmpPrefix + pPath.replace(/^\//, '');
	}

	collection(pKey) { return _COLLECTIONS[pKey] ? Object.assign({ Key: pKey }, _COLLECTIONS[pKey]) : null; }

	collectionList() { return Object.keys(_COLLECTIONS).map((pKey) => Object.assign({ Key: pKey }, _COLLECTIONS[pKey])); }

	/** The recolored-SVG URL for a single shape. Colors are optional; the server applies its defaults. */
	shapeURL(pCollection, pName, pColors)
	{
		let tmpColors = pColors || {};
		let tmpParams = [];
		if (tmpColors.pri) { tmpParams.push('pri=' + encodeURIComponent(String(tmpColors.pri).replace('#', ''))); }
		if (tmpColors.sec) { tmpParams.push('sec=' + encodeURIComponent(String(tmpColors.sec).replace('#', ''))); }
		if (tmpColors.op != null && tmpColors.op !== '') { tmpParams.push('op=' + encodeURIComponent(tmpColors.op)); }
		let tmpURL = this._url('Shapes/' + encodeURIComponent(pCollection) + '/' + encodeURIComponent(pName));
		return tmpParams.length ? (tmpURL + '?' + tmpParams.join('&')) : tmpURL;
	}

	/** Ranked search across the collections (or one, when pCollection is set). Empty array on failure. */
	search(pQuery, pCollection, pLimit)
	{
		let tmpParams = [];
		if (pQuery) { tmpParams.push('q=' + encodeURIComponent(pQuery)); }
		if (pCollection) { tmpParams.push('collection=' + encodeURIComponent(pCollection)); }
		if (pLimit) { tmpParams.push('limit=' + encodeURIComponent(pLimit)); }
		let tmpURL = this._url('Shapes/Search') + (tmpParams.length ? ('?' + tmpParams.join('&')) : '');
		return libFrameworkClient.getJSON(this.fable, tmpURL)
			.then((pBody) => (pBody && Array.isArray(pBody.Shapes)) ? pBody.Shapes : [])
			.catch(() => []);
	}
}

module.exports = PictProviderShapesAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
